import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Field, Textarea } from '@/components/ui/controls'
import { DataTable } from '@/components/ui/data-table'
import { toast as message } from 'sonner'
import { StatusBadge } from '../products/PageHelpers'
import { ArrowLeft, Download, FileText, Undo2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import {
  createRefundRequest,
  downloadOrderInvoice,
  getOrder,
  getOrderRefundRequests,
  getOrderRefunds,
  issueOrderInvoice,
  type IssueOrderInvoiceRequest,
} from '../../services/order'
import { formatDate, formatTimestamp } from '../../utils/format'
import {
  formatAmount,
  formatDisplayExchangeRate,
} from '../../utils/orderDisplay'
import {
  orderDisplayStatus,
  orderStatusColors,
  orderStatusLabels,
  shouldShowOrderFees,
} from '../../utils/orderDisplayStatus'
import { billingReasonText } from '../subscriptions/subscriptionLabels'
import { useMerchantStore } from '../../stores/merchantStore'
import type { Order, Refund, RefundRequest } from '../../types'

const paymentMethodLabels: Record<string, string> = {
  CREDIT_CARD: '信用卡',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  PAYPAL: 'PayPal',
  WECHAT_PAY: '微信支付',
  ALIPAY: '支付寶',
  ALIPAY_HK: 'AlipayHK',
  BOOST: 'Boost',
  TOUCH_N_GO_EWALLET: "Touch'n Go",
  LINE_PAY: 'LINE Pay',
  KREDIVO: 'Kredivo',
  GCASH: 'GCash',
  JKOPAY: 'JKOPay',
  MIR: 'Mir',
  SBP: 'SBP',
  T_PAY: 'T-Pay',
  SBER_PAY: 'Sber Pay',
  YOO_MONEY: 'YooMoney',
  PAYNOW: 'PayNow',
  QRIS: 'QRIS',
  OVO: 'OVO SNAP',
  DANA: 'DANA',
  SHOPEE_PAY: 'ShopeePay',
  COINS_PH: 'Coins.ph',
  GO_PAY: 'GoPay',
  GRAB_PAY: 'GrabPay',
  MAYA: 'Maya',
  MOMO: 'MoMo',
  TRUE_MONEY: 'TrueMoney',
  ZALO_PAY: 'ZaloPay',
  PROMPT_PAY: 'PromptPay',
  VIET_QR: 'VietQR',
  DUITNOW: 'DuitNow',
  VIRTUAL_ACCOUNT: 'Virtual Account / 銀行轉賬',
  CRYPTO: '加密貨幣',
  USDT_DIRECT: 'USDT Direct',
}

const refundRequestStatusMeta: Record<
  string,
  { color: string; label: string }
> = {
  PENDING: { color: 'gold', label: '待審核' },
  REVIEWING: { color: 'blue', label: '審核中' },
  APPROVED: { color: 'green', label: '已批准' },
  REJECTED: { color: 'red', label: '已拒絕' },
}

function formatOptionalAmount(value?: string | null, currency?: string) {
  return value == null ? '-' : `${formatAmount(value)} ${currency || ''}`.trim()
}

function formatOptionalUsdAmount(value?: string | null) {
  return value == null ? '-' : `${formatAmount(value)} USD`
}

function formatOptionalRate(value?: string | null) {
  if (value == null) return '-'
  const numeric = Number(value)
  return Number.isFinite(numeric) ? `${(numeric * 100).toFixed(2)}%` : '-'
}

function formatRateAndPerTransactionFee(
  rate?: string | null,
  perTransactionFee?: string | null,
) {
  return `${formatOptionalRate(rate)} / ${formatOptionalUsdAmount(perTransactionFee)}`
}

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>()
  const [order, setOrder] = useState<Order | null>(null)
  const [refundRequests, setRefundRequests] = useState<RefundRequest[]>([])
  const [refunds, setRefunds] = useState<Refund[]>([])
  const [loading, setLoading] = useState(true)
  const [refundModalOpen, setRefundModalOpen] = useState(false)
  const [refundAmount, setRefundAmount] = useState('')
  const [refundReason, setRefundReason] = useState('')
  const [submittingRefund, setSubmittingRefund] = useState(false)
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false)
  const [invoiceDownloading, setInvoiceDownloading] = useState(false)
  const [invoiceValues, setInvoiceValues] = useState<IssueOrderInvoiceRequest>({
    recipientName: '',
    recipientAddress: '',
    recipientEmail: '',
  })
  const merchant = useMerchantStore((state) => state.merchant)
  const navigate = useNavigate()

  const loadRefundData = async (orderId: string) => {
    const [requestRes, refundRes] = await Promise.all([
      getOrderRefundRequests(orderId),
      getOrderRefunds(orderId),
    ])
    setRefundRequests(requestRes.data)
    setRefunds(refundRes.data)
  }

  useEffect(() => {
    if (id) {
      Promise.all([getOrder(id), loadRefundData(id)])
        .then(([res]) => setOrder(res.data))
        .finally(() => setLoading(false))
    }
  }, [id])

  const hasActiveRefundRequest = refundRequests.some(
    (request) => request.status === 'PENDING' || request.status === 'REVIEWING',
  )
  const canRequestRefund =
    merchant?.refundable === true &&
    (order?.status === 'PAID' || order?.status === 'SETTLED') &&
    !hasActiveRefundRequest
  const canManageInvoice =
    merchant?.invoiceEnabled === true &&
    order?.type === 'PAYMENT' &&
    order.paidAt != null
  const orderAmount = order ? (order.baseAmount ?? order.amount) : undefined
  const payableAmount = order
    ? (order.payableAmount ?? order.amount)
    : undefined

  const openRefundModal = () => {
    if (!payableAmount) return
    setRefundAmount(payableAmount)
    setRefundReason('')
    setRefundModalOpen(true)
  }

  const submitRefundRequest = async () => {
    if (!id) return
    if (!refundAmount || !refundReason.trim()) {
      message.warning('請填寫退款金額和原因')
      return
    }
    setSubmittingRefund(true)
    try {
      await createRefundRequest(id, {
        amount: refundAmount,
        reason: refundReason.trim(),
      })
      message.success('退款申請已提交')
      setRefundModalOpen(false)
      await loadRefundData(id)
    } catch (e) {
      message.error(e instanceof Error ? e.message : '提交失敗')
    } finally {
      setSubmittingRefund(false)
    }
  }

  const saveInvoicePdf = (blob: Blob) => {
    if (!id) return
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `invoice-${id}.pdf`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  const downloadExistingInvoice = async () => {
    if (!id) return
    setInvoiceDownloading(true)
    try {
      saveInvoicePdf(await downloadOrderInvoice(id))
    } catch (e) {
      message.error(e instanceof Error ? e.message : '下載 Invoice 失敗')
    } finally {
      setInvoiceDownloading(false)
    }
  }

  const openInvoiceModal = () => {
    setInvoiceValues({
      recipientName: '',
      recipientAddress: '',
      recipientEmail: '',
    })
    setInvoiceModalOpen(true)
  }

  const issueInvoice = async (values: IssueOrderInvoiceRequest) => {
    if (!id) return
    setInvoiceDownloading(true)
    try {
      const blob = await issueOrderInvoice(id, {
        recipientName: values.recipientName.trim(),
        recipientAddress: values.recipientAddress.trim(),
        recipientEmail: values.recipientEmail?.trim() || undefined,
      })
      saveInvoicePdf(blob)
      const refreshed = await getOrder(id)
      setOrder(refreshed.data)
      setInvoiceModalOpen(false)
      message.success('Invoice 已簽發')
    } catch (e) {
      message.error(e instanceof Error ? e.message : '簽發 Invoice 失敗')
    } finally {
      setInvoiceDownloading(false)
    }
  }

  if (loading) {
    return (
      <div
        role="status"
        className="flex min-h-40 items-center justify-center text-muted-foreground"
      >
        載入中…
      </div>
    )
  }

  if (!order) {
    return <div>訂單不存在</div>
  }

  const displayStatus = orderDisplayStatus(order.status, order.refundStatus)
  const showFees = shouldShowOrderFees(order.status)

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 24,
        }}
      >
        <div className={'flex flex-wrap items-center gap-2'}>
          <Button
            onClick={() => navigate('/dashboard/orders')}
            variant={'outline'}
            type="button"
          >
            {<ArrowLeft />}返回訂單列表
          </Button>
          <h2
            style={{ margin: 0 }}
            className="mb-4 text-xl font-semibold tracking-tight"
          >
            訂單詳情
          </h2>
        </div>
        <div className={'flex flex-wrap items-center gap-2'}>
          {canManageInvoice && (
            <Button
              onClick={
                order.invoiceIssued ? downloadExistingInvoice : openInvoiceModal
              }
              variant={'outline'}
              disabled={Boolean(invoiceDownloading)}
              type="button"
            >
              {order.invoiceIssued ? <Download /> : <FileText />}
              {order.invoiceIssued ? '下載 Invoice' : '簽發並下載 Invoice'}
            </Button>
          )}
          {canRequestRefund && (
            <Button onClick={openRefundModal} variant={'outline'} type="button">
              {<Undo2 />}申請退款
            </Button>
          )}
        </div>
      </div>
      <Card>
        <div className="space-y-4 px-5">
          <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border bg-border md:grid-cols-2">
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">{'訂單號'}</dt>
              <dd className="break-words text-sm">{order.id}</dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">{'狀態'}</dt>
              <dd className="break-words text-sm">
                <StatusBadge color={orderStatusColors[displayStatus]}>
                  {orderStatusLabels[displayStatus] || displayStatus}
                </StatusBadge>
              </dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'客戶郵箱'}
              </dt>
              <dd className="break-words text-sm">
                {order.customerEmail || '-'}
              </dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'客戶名稱'}
              </dt>
              <dd className="break-words text-sm">
                {order.customerName || '-'}
              </dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'訂單金額'}
              </dt>
              <dd className="break-words text-sm">
                {formatAmount(orderAmount ?? order.amount)} {order.currency}
              </dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'應付金額'}
              </dt>
              <dd className="break-words text-sm">
                {formatAmount(payableAmount ?? order.amount)} {order.currency}
              </dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">{'匯率'}</dt>
              <dd className="break-words text-sm">
                {formatDisplayExchangeRate(order.currency, order.exchangeRate)}
              </dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'應付金額(USD)'}
              </dt>
              <dd className="break-words text-sm">
                {formatOptionalUsdAmount(order.grossAmountUsd)}
              </dd>
            </div>
            {showFees && (
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'平台費率/平台每筆手續費'}
                </dt>
                <dd className="break-words text-sm">
                  {formatRateAndPerTransactionFee(
                    order.platformFeeRate,
                    order.perTransactionFee,
                  )}
                </dd>
              </div>
            )}
            {showFees && (
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'平台費用(USD)'}
                </dt>
                <dd className="break-words text-sm">
                  {order.platformFeeUsd
                    ? `${formatAmount(order.platformFeeUsd)} USD`
                    : `${formatAmount(order.platformFee)} ${order.currency}`}
                </dd>
              </div>
            )}
            {showFees && (
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'商戶分擔費率/商戶分擔每筆手續費'}
                </dt>
                <dd className="break-words text-sm">
                  {formatRateAndPerTransactionFee(
                    order.merchantFeeRate,
                    order.merchantPerTransactionFee,
                  )}
                </dd>
              </div>
            )}
            {showFees && (
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'客戶分擔費率/客戶分擔每筆手續費'}
                </dt>
                <dd className="break-words text-sm">
                  {formatRateAndPerTransactionFee(
                    order.payerFeeRate,
                    order.payerPerTransactionFee,
                  )}
                </dd>
              </div>
            )}
            {showFees && (
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'商戶分擔平台手續費(USD)'}
                </dt>
                <dd className="break-words text-sm">
                  {formatOptionalUsdAmount(order.merchantFeeUsd)}
                </dd>
              </div>
            )}
            {showFees && (
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'客戶分擔平台手續費(USD)'}
                </dt>
                <dd className="break-words text-sm">
                  {formatOptionalUsdAmount(order.payerFeeUsd)}
                </dd>
              </div>
            )}
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'循環保證金'}
              </dt>
              <dd className="break-words text-sm">
                {formatOptionalAmount(
                  order.rollingReserveAmount,
                  order.currency,
                )}
              </dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'保證金釋放時間'}
              </dt>
              <dd className="break-words text-sm">
                {formatDate(order.rollingReserveReleaseAt)}
              </dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'實際結算金額'}
              </dt>
              <dd className="break-words text-sm">
                {order.settledAmount
                  ? `${formatAmount(order.settledAmount)} USD`
                  : '-'}
              </dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'支付方式'}
              </dt>
              <dd className="break-words text-sm">
                {paymentMethodLabels[order.paymentMethod] ||
                  order.paymentMethod}
              </dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'產品 ID'}
              </dt>
              <dd className="break-words text-sm">{order.productId}</dd>
            </div>
            {order.productSnapshot && (
              <>
                <div className="min-w-0 bg-white p-3">
                  <dt className="mb-1 text-xs text-muted-foreground">
                    {'產品名稱（快照）'}
                  </dt>
                  <dd className="break-words text-sm">
                    {order.productSnapshot.name}
                  </dd>
                </div>
                <div className="min-w-0 bg-white p-3">
                  <dt className="mb-1 text-xs text-muted-foreground">
                    {'產品價格（快照）'}
                  </dt>
                  <dd className="break-words text-sm">
                    {formatAmount(order.productSnapshot.price)}{' '}
                    {order.productSnapshot.currency}
                  </dd>
                </div>
              </>
            )}
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'創建時間'}
              </dt>
              <dd className="break-words text-sm">
                {formatTimestamp(order.createdAt)}
              </dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'支付時間'}
              </dt>
              <dd className="break-words text-sm">
                {formatTimestamp(order.paidAt)}
              </dd>
            </div>
          </dl>
        </div>
      </Card>

      {order.subscription && (
        <Card style={{ marginTop: 16 }}>
          <div className="space-y-4 px-5">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-4">
              <h3 className="font-semibold">{'訂閱資訊'}</h3>
            </div>
            <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border bg-border md:grid-cols-2">
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'訂閱 ID'}
                </dt>
                <dd className="break-words text-sm">
                  <Button
                    onClick={() =>
                      navigate(
                        `/dashboard/subscriptions/${order.subscription!.subscriptionId}`,
                      )
                    }
                    style={{ padding: 0 }}
                    variant={'link'}
                    type="button"
                  >
                    {order.subscription.subscriptionId}
                  </Button>
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'賬單 ID'}
                </dt>
                <dd className="break-words text-sm">
                  {order.subscription.subscriptionInvoiceId}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'週期號'}
                </dt>
                <dd className="break-words text-sm">
                  #{order.subscription.cycleNo}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'計費原因'}
                </dt>
                <dd className="break-words text-sm">
                  {billingReasonText[order.subscription.billingReason] ||
                    order.subscription.billingReason}
                </dd>
              </div>
            </dl>
          </div>
        </Card>
      )}

      <Card style={{ marginTop: 16 }}>
        <div className="space-y-4 px-5">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-4">
            <h3 className="font-semibold">{'退款申請'}</h3>
          </div>
          <DataTable
            rowKey={'id'}
            columns={[
              {
                key: 'id',
                header: '申請號',
                render: (row: RefundRequest) => row['id'] ?? '-',
              },
              {
                key: 'column-1',
                header: '金額',
                className: 'text-right tabular-nums',
                render: (row: RefundRequest) => {
                  const r = row
                  return `${formatAmount(r.amount)} ${r.currency}`
                },
              },
              {
                key: 'status',
                header: '狀態',
                render: (row: RefundRequest) => {
                  const v = row['status']
                  const meta = refundRequestStatusMeta[v] || {
                    color: 'default',
                    label: v,
                  }
                  return (
                    <StatusBadge color={meta.color}>{meta.label}</StatusBadge>
                  )
                },
              },
              {
                key: 'reason',
                header: '原因',
                render: (row: RefundRequest) => row['reason'] ?? '-',
              },
              {
                key: 'adminNote',
                header: '審核備註',
                render: (row: RefundRequest) => {
                  const v = row['adminNote']
                  return v || '-'
                },
              },
              {
                key: 'createdAt',
                header: '提交時間',
                className: 'text-xs text-muted-foreground',
                render: (row: RefundRequest) => {
                  const v = row['createdAt']
                  return formatTimestamp(v)
                },
              },
            ]}
            data={refundRequests}
            emptyText={{ emptyText: '暫無退款申請' }.emptyText}
          />
        </div>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <div className="space-y-4 px-5">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-4">
            <h3 className="font-semibold">{'退款記錄'}</h3>
          </div>
          <DataTable
            rowKey={'id'}
            columns={[
              {
                key: 'id',
                header: '退款號',
                render: (row: Refund) => row['id'] ?? '-',
              },
              {
                key: 'column-1',
                header: '金額',
                className: 'text-right tabular-nums',
                render: (row: Refund) => {
                  const r = row
                  return `${formatAmount(r.amount)} ${r.currency}`
                },
              },
              {
                key: 'status',
                header: '狀態',
                render: (row: Refund) => {
                  const v = row['status']
                  return (
                    <StatusBadge
                      color={
                        v === 'SUCCEEDED'
                          ? 'green'
                          : v === 'FAILED'
                            ? 'red'
                            : 'gold'
                      }
                    >
                      {v}
                    </StatusBadge>
                  )
                },
              },
              {
                key: 'reason',
                header: '原因',
                render: (row: Refund) => row['reason'] ?? '-',
              },
              {
                key: 'succeededAt',
                header: '完成時間',
                className: 'text-xs text-muted-foreground',
                render: (row: Refund) => {
                  const v = row['succeededAt']
                  return formatTimestamp(v)
                },
              },
            ]}
            data={refunds}
            emptyText={{ emptyText: '暫無退款記錄' }.emptyText}
          />
        </div>
      </Card>

      <Modal
        title={'簽發 Invoice'}
        open={invoiceModalOpen}
        onOpenChange={(open) => {
          if (!open) (() => setInvoiceModalOpen(false))()
        }}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setInvoiceModalOpen(false)}
            >
              {'取消'}
            </Button>
            <Button
              disabled={Boolean(invoiceDownloading)}
              type="submit"
              form="invoice-form"
            >
              {'簽發並下載'}
            </Button>
          </>
        }
      >
        <form
          id="invoice-form"
          onSubmit={(event) => {
            event.preventDefault()
            if (
              !invoiceValues.recipientName.trim() ||
              !invoiceValues.recipientAddress.trim()
            ) {
              message.warning('請輸入被開票方名稱和地址')
              return
            }
            void issueInvoice(invoiceValues)
          }}
          className="space-y-5"
        >
          <Field label="被開票方名稱" htmlFor="invoice-name" required>
            <Input
              id="invoice-name"
              name="recipientName"
              autoComplete="organization"
              required
              maxLength={200}
              value={invoiceValues.recipientName}
              onChange={(event) =>
                setInvoiceValues({
                  ...invoiceValues,
                  recipientName: event.target.value,
                })
              }
            />
          </Field>
          <Field label="被開票方地址" htmlFor="invoice-address" required>
            <Textarea
              id="invoice-address"
              name="recipientAddress"
              autoComplete="street-address"
              required
              maxLength={500}
              rows={3}
              value={invoiceValues.recipientAddress}
              onChange={(event) =>
                setInvoiceValues({
                  ...invoiceValues,
                  recipientAddress: event.target.value,
                })
              }
            />
          </Field>
          <Field label="聯絡郵箱（選填）" htmlFor="invoice-email">
            <Input
              id="invoice-email"
              name="recipientEmail"
              autoComplete="email"
              type="email"
              maxLength={254}
              value={invoiceValues.recipientEmail || ''}
              onChange={(event) =>
                setInvoiceValues({
                  ...invoiceValues,
                  recipientEmail: event.target.value,
                })
              }
            />
          </Field>
        </form>
      </Modal>

      <Modal
        title={'申請退款'}
        open={refundModalOpen}
        onOpenChange={(open) => {
          if (!open) (() => setRefundModalOpen(false))()
        }}
        footer={
          <>
            <Button variant="outline" onClick={() => setRefundModalOpen(false)}>
              {'取消'}
            </Button>
            <Button
              disabled={Boolean(submittingRefund)}
              onClick={submitRefundRequest}
            >
              {'提交申請'}
            </Button>
          </>
        }
      >
        <div style={{ width: '100%' }} className={'flex flex-col gap-3'}>
          <span className="text-muted-foreground">
            默認退款金額為用戶實際支付的應付金額。
          </span>
          <div className="flex items-center gap-2">
            <Input
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              placeholder={'退款金額'}
            />
            {order.currency}
          </div>
          <div style={{ marginBottom: 24 }}>
            <Textarea
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder={'退款原因'}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}
